<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="[
                {
                    title: 'Name',
                    field: 'name',
                    sorter: 'string',
                    minWidth: 150,
                },
                {
                    title: 'ID', 
                    field: 'id', 
                    sorter: 'string',
                    minWidth: 300,
                },
                {
                    title: 'Secret',
                    field: 'secret',
                    sorter: 'string',
                    minWidth: 400,
                },
                {
                    title: 'Scopes',
                    field: 'permissions_for_human',
                    minWidth: 400,
                },
                {
                    title: 'Status', 
                    field: 'revoked', 
                    minWidth: 130,
                    formatter(cell, params) {
                        if (cell._cell.value) {
                            return `<span class='badge badge-red'>Inactive</span>`;
                        }

                        return `<span class='badge badge-green'>Active</span>`;
                    }
                },
            ]"
            :actions="actions"
            :refresh.sync="refreshTable"
            ajax-path="admin/oauth/clients"
            @ajaxResponse="(response) => {
                $store.commit('admin/apps/setItems', response);
            }"
        />
        <app-modal
            :show.sync="showEditModal"
            width="md:w-1/2 lg:w-1/4"
            @close="editModalClosed"
        >
            <template slot="header">Edit User</template>
            <template slot="body">                
                <app-input
                    v-model="editForm.name"
                    placeholder="Name"
                    :error="editForm.errors.get('name')"
                    @updated="editForm.errors.clear('name')"
                    focus
                    required
                />
                <app-select
                    v-model="editForm.permissions"
                    placeholder="Scopes"
                    :error="editForm.errors.get('permissions')"
                    @updated="editForm.errors.clear('permissions')"
                    :options="items.permissions"
                    value-key="id"
                    label-key="name"
                    multiple
                    required
                />
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showEditModal = false"
                        :disable="editFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="update"
                        :loading="editFormIsLoading"
                    >Save</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAddModal"
            width="md:w-1/2 lg:w-1/4"
            @close="addModalClosed"
        >
            <template slot="header">Create Application</template>
            <template slot="body">
                <app-input
                    v-model="addForm.name"
                    placeholder="Name"
                    :error="addForm.errors.get('name')"
                    @updated="addForm.errors.clear('name')"
                    focus
                    required
                />    
                <app-select
                    v-model="addForm.permissions"
                    placeholder="Scopes"
                    :error="addForm.errors.get('permissions')"
                    @updated="addForm.errors.clear('permissions')"
                    :options="items.permissions"
                    value-key="id"
                    label-key="name"
                    multiple
                    required
                />            
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showAddModal = false"
                        :disable="addFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="store"
                        :loading="addFormIsLoading"
                    >Create</app-button>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Form from '@/helpers/Form';
    import debounce from 'lodash/debounce';

    export default {
        data() {
            return {
                addForm: new Form({
                    name: null,
                    permissions: [],
                }),
                editForm: new Form({
                    id: null,
                    name: null,
                    permissions: [],
                }),
                editFormIsLoading: false,
                addFormIsLoading: false,
                showEditModal: false,
                showAddModal: false,
                refreshTable: false,
                actions: [
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        handler: apps => {
                            this.showEditModal = true;
                            this.editForm.set(apps[0]);
                        },
                        requiredSelect: true,
                    },
                    {
                        label: 'Reset Secret',
                        icon: () => import('@/icons/Update.vue'),
                        handler: apps => {
                            this.resetSecret(apps[0]);
                        },
                        requiredSelect: true,
                    },
                    {
                        label: 'Activate',
                        icon: () => import('@/icons/VisibilityOutlined.vue'),
                        requiredSelect: true,
                        handler: apps => {
                            this.activate(apps[0]);
                        },
                        isEligible(apps) {
                            return apps[0].soft_deleted;
                        }
                    },
                    {
                        label: 'Deactivate',
                        icon: () => import('@/icons/VisibilityOffOutlined.vue'),
                        requiredSelect: true,
                        handler: apps => {
                            this.deactivate(apps[0]);
                        },
                        isEligible(apps) {
                            return !apps[0].soft_deleted && !apps[0].super_admin;
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        requiredSelect: true,
                        handler: apps => {
                            this.deleteApp(apps[0]);
                        }
                    },
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: () => {
                            this.showAddModal = true;
                        }
                    },                    
                ],
            }
        },
        computed: {
            ...mapGetters({
                items: 'admin/apps/items',
            }),
        },
        methods: {
            edit(app) {
                this.showEditModal = true;
                this.editForm.set(app);
            },
            editModalClosed() {
                this.editForm.reset();
                this.editForm.errors.clear();                
            },
            update() {
                this.editFormIsLoading = true;
                this.$store.dispatch('admin/apps/update', this.editForm.payload())
                    .then(() => {
                        this.editFormIsLoading = false;
                        this.showEditModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Application was updated successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.editFormIsLoading = false;
                        this.editForm.errors.record(response.data);
                    });
            },
            addModalClosed() {
                this.addForm.reset();
                this.addForm.errors.clear();
            },
            store() {
                this.addFormIsLoading = true;
                this.$store.dispatch('admin/apps/store', this.addForm.payload())
                    .then(() => {
                        this.addFormIsLoading = false;
                        this.showAddModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Application was created successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.addFormIsLoading = false;
                        this.addForm.errors.record(response.data);
                    })
            },
            deactivate(app) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to deactivate "<strong>${app.name}</strong>" application.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, deactivate it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('admin/apps/deactivate', app.id);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'User was successfully deactivated.'
                        })
                    }
                })                
            },
            activate(app) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to activate "<strong>${app.name}</strong>" application.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, activate it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('admin/apps/activate', app.id);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'User was successfully activated.'
                        })
                    }
                })                
            },
            deleteApp(app) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete "<strong>${app.name}</strong>" application permanently.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('admin/apps/delete', app.id);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'User was successfully deleted.'
                        })
                    }
                })                
            },
            resetSecret(app) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to reset the secret of ${app.name} application.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, reset it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('admin/apps/resetSecret', app.id);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Secret was successfully reset.'
                        })
                    }
                })
            },
        }
    }
</script>